import React, { useState } from 'react';
import { Card, Form, Typography, Divider, Alert, Checkbox } from 'antd';
import {
  FileOutlined,
  FolderOpenOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { DirectoryDeleteParams } from '@core/@models/ApiManualModel';
import { DirectoryTree } from '../shared/DirectoryTree';
import { FileSelector } from '../shared/FileSelector';
import { CodeSnippetDisplay } from '../shared/CodeSnippetDisplay';

const { Text } = Typography;

export const DeleteTab: React.FC = () => {
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [selectedPath, setSelectedPath] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [isPattern, setIsPattern] = useState<boolean>(false);
  const [isRecursive, setIsRecursive] = useState<boolean>(false);

  const handleFolderSelect = (folderId: number | null, path: string) => {
    setSelectedFolderId(folderId);
    setSelectedPath(path);
    setSelectedFile('');
    setIsPattern(false);
  };

  const handleFileSelect = (filename: string, pattern: boolean) => {
    setSelectedFile(filename);
    setIsPattern(pattern);
  };

  const isFormComplete = () => {
    return selectedPath && selectedFile;
  };

  const getParams = (): DirectoryDeleteParams => {
    return {
      directoryServicePath: selectedPath,
      fileList: selectedFile,
      isPattern,
      isRecursive,
    };
  };

  return (
    <div className="directory-delete-tab">
      <Card size="small" className="info-card">
        <Text type="secondary">
          <strong>POST /external/del</strong> - Delete file(s) from directory
          service. Supports wildcard patterns for batch deletion.
        </Text>
      </Card>

      <Alert
        message={
          <span>
            <WarningOutlined style={{ marginRight: 8 }} />
            Warning: Deletion is Permanent
          </span>
        }
        description="Files deleted through this API cannot be recovered. Please verify the file selection before executing the delete command."
        type="error"
        showIcon={false}
        style={{ marginBottom: 16 }}
      />

      <Divider />

      <Form layout="vertical">
        <Form.Item label="Folder" required>
          <DirectoryTree
            onSelect={handleFolderSelect}
            selectedPath={selectedPath}
          />
        </Form.Item>

        <Form.Item label="File(s) to Delete" required>
          <FileSelector
            folderId={selectedFolderId}
            value={selectedFile}
            onChange={handleFileSelect}
            mode="delete"
            folderPath={selectedPath}
            isRecursive={isRecursive}
          />
        </Form.Item>

        <Form.Item
          help={
            isRecursive
              ? 'Deletion will include matching files in all subdirectories under the selected folder'
              : 'Only files in the selected folder (no subdirectories)'
          }
        >
          <Checkbox
            checked={isRecursive}
            onChange={(e) => setIsRecursive(e.target.checked)}
          >
            Recursive (remove into subdirectories)
          </Checkbox>
        </Form.Item>

        {isFormComplete() && (
          <Form.Item label="Delete scope">
            <div
              style={{
                padding: 12,
                background: '#fafafa',
                borderRadius: 4,
                border: '1px solid #f0f0f0',
                fontFamily: 'monospace',
                fontSize: 12,
              }}
            >
              <div style={{ marginBottom: 4 }}>
                <WarningOutlined style={{ marginRight: 6, color: '#faad14' }} />
                <Text strong>
                  {isRecursive
                    ? 'Folder + subdirectories'
                    : 'Current folder only'}
                </Text>
              </div>
              <div style={{ paddingLeft: 8, fontSize: 11, color: '#666' }}>
                <FolderOpenOutlined
                  style={{ marginRight: 6, color: '#faad14', fontSize: 11 }}
                />
                {selectedPath}
                {isRecursive && (
                  <span style={{ color: '#8c8c8c', marginLeft: 4 }}>
                    (and all nested folders)
                  </span>
                )}
              </div>
              <div
                style={{
                  paddingLeft: 8,
                  marginTop: 4,
                  fontSize: 11,
                  color: '#595959',
                }}
              >
                <FileOutlined
                  style={{ marginRight: 6, fontSize: 11, color: '#1890ff' }}
                />
                {isPattern ? (
                  <>Pattern: {selectedFile}</>
                ) : (
                  <>File: {selectedFile}</>
                )}
              </div>
            </div>
          </Form.Item>
        )}
      </Form>

      <Divider />
      {isFormComplete() && isPattern && (
        <Alert
          message="Wildcard Pattern Detected"
          description={`Pattern "${selectedFile}" will delete ALL matching files${
            isRecursive ? ' in the folder and all subdirectories' : ''
          }. Make sure to verify the preview above before executing.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <CodeSnippetDisplay
        apiType="directory-delete"
        params={getParams()}
        disabled={!isFormComplete()}
      />
    </div>
  );
};
